import React, { useEffect, useRef, useState } from "react";
import SvgIcn from "../../data/IconCompo";
import countries from "../../data/countries.json";
import CountryFlag from "../../data/IconCountry";
import { fetchImage } from "../../services/itemsServices";
import TagZ from "../atoms/TagZ";
import { likeItem } from "../../services/UserService";
import { useDispatch } from "react-redux";
import { showPopup } from "../../redux/actions";
import { isAuthenticated } from "../../auth/authApi";
import { toggleFavorite } from "../../services/pbFavorites";
import { BACKEND_URL } from "../../config";
import { trackCardClick } from "../../services/analytics";

function csvLogoCdnUrl(img) {
  if (!img) return null;
  const base =
    process.env.REACT_APP_ITEM_ICONS_BASE || `${BACKEND_URL}/images/items`;
  return `${base}/${encodeURIComponent(img)}`;
}

/**
 * App Store / Play + main site links must be absolute https — avoids SPA-relative mistakes.
 * CSV rows often store bare hostnames (e.g. freepik.com); treat those as https.
 */
function ensureAbsoluteHttpUrl(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (/^(mailto:|tel:)/i.test(s)) return s;
  if (/\s/.test(s)) return "";
  if (s.startsWith("/") || s.startsWith(".")) return "";
  if (
    s.includes(".") &&
    /^[\w.-]+(?:\/[\w./?#&=%+~!$'()[\]*,-]*)?$/i.test(s)
  ) {
    return `https://${s}`;
  }
  return "";
}

/** Opens in a new tab in the same synchronous turn as the click (required for popup rules). */
function openInNewTabReliable(url) {
  const u = ensureAbsoluteHttpUrl(url);
  if (!u) return false;
  const a = document.createElement("a");
  a.href = u;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

const ItemCard = ({
  data,
  selectedMode,
  page,
  isFavorite,
  toogleLike = () => {},
  /** PB `user_favorites.itemId` (may differ from data._id for legacy rows). */
  favoriteToggleId,
  /** Bundled URL from `getCardsLogoUrl` (instant load for CSV cards). */
  imageUrl: imageUrlProp,
  /** When the bundled logo is raster (WebP or PNG), not SVG. */
  bundledLogoIsWebp,
  demo,
}) => {
  const isAppsCard =
    String(selectedMode || "").toLowerCase() === "apps" ||
    String(data?.category || "").toLowerCase() === "apps";
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const [hideLastTag, setHideLastTag] = useState(false);
  const tagsRowRef = useRef(null);
  const dispatch = useDispatch();
  const bundleUrl = imageUrlProp || data?.imageUrl;

  useEffect(() => {
    setPhotoBroken(false);
  }, [data?._id, bundleUrl, data?.image, data?.img]);

  useEffect(() => {
    const checkTagWrap = () => {
      const row = tagsRowRef.current;
      if (!row) {
        setHideLastTag(false);
        return;
      }
      const children = Array.from(row.children || []);
      if (children.length < 2) {
        setHideLastTag(false);
        return;
      }
      const firstTop = children[0].offsetTop;
      const wrapped = children.some((el) => el.offsetTop > firstTop);
      setHideLastTag(wrapped);
    };

    checkTagWrap();
    window.addEventListener("resize", checkTagWrap);
    return () => window.removeEventListener("resize", checkTagWrap);
  }, [data?.tags]);

  const handleCardHover = () => {
    setIsCardHovered(true);
  };

  const handleCardOut = () => {
    setIsCardHovered(false);
  };
  const handleLike = (e) => {
    e.stopPropagation();
    if (isAuthenticated()) {
      const pbId = isFavorite
        ? data.__pbFavoriteKeys?.[0] ?? favoriteToggleId ?? data._id
        : data._id;
      if (pbId == null || String(pbId).trim() === "") return;
      const modeRaw = selectedMode ?? data?.category;
      const category =
        typeof modeRaw === "string" && modeRaw.trim()
          ? modeRaw.trim().toLowerCase()
          : "tools";
      toogleLike(data);
      void toggleFavorite(pbId, category).catch((err) => {
        console.warn("Favorite sync failed", err);
      });
    } else {
      dispatch(showPopup("login"));
    }
  };

  const handleStoreClick = (e, storeKey, targetUrl) => {
    e.preventDefault();
    e.stopPropagation();
    if (!openInNewTabReliable(targetUrl)) return;
    if (data?._id) {
      void trackCardClick({
        itemId: data._id,
        category: "apps",
        clickTarget: storeKey === "apple" ? "app_store" : "google_play",
      });
    }
  };

  const appStoreUrl = String(data?.linkApple || "").trim();
  const playStoreUrl = String(data?.linkAndroid || "").trim();

  return (
    <div
      className={`item-card ${page ? `${page.toLowerCase()}-page` : ""} ${
        isAppsCard ? "apps-card" : ""
      }`}
      onMouseOver={isAppsCard ? undefined : handleCardHover}
      onMouseOut={isAppsCard ? undefined : handleCardOut}
      onClick={
        isAppsCard
          ? undefined
          : () => {
              if (!data?.link) return;
              const opened = openInNewTabReliable(data.link);
              if (!opened) return;
              if (data?._id) {
                const cat = String(
                  selectedMode || data?.category || ""
                ).toLowerCase();
                void trackCardClick({
                  itemId: data._id,
                  category: cat,
                  clickTarget: "open_link",
                });
              }
            }
      }
    >
      <div className="fav-icon-box">
        {!demo &&
          (isFavorite ? (
            <SvgIcn name={"fav"} className={"selected"} onClick={handleLike} />
          ) : (
            <SvgIcn name={"fav"} className={""} onClick={handleLike} />
          ))}
      </div>

      <div
        className={`photo${bundledLogoIsWebp ? " photo--webp-logo" : ""}`}
      >
        {(() => {
          const b64 =
            data?.image && data.image !== "notloaded" ? data.image : null;
          const cdn =
            !bundleUrl && !b64 && data?._source === "csv" && data?.img
              ? csvLogoCdnUrl(data.img)
              : null;
          const src = bundleUrl
            ? bundleUrl
            : b64
            ? `data:image/svg+xml;base64,${b64}`
            : demo && data?.img
            ? data.img
            : cdn;
          if (src && !photoBroken) {
            return (
              <img
                src={src}
                alt=""
                className="img"
                onError={() => setPhotoBroken(true)}
              />
            );
          }
          return (
            <div className="avatar-skl">
              <SvgIcn name="logo01" />
            </div>
          );
        })()}
      </div>
      <div className={`title `}>
        <h2 className={`name ${isCardHovered && `${selectedMode}-text`}`}>
          {data?.name || <div className="name-skl"></div>}
        </h2>
      </div>
      <p className="intro">
        {data?.description?.[0] || (
          <div className="intro-skl">
            <div className="intro-skl-lines"></div>
            <div className="intro-skl-lines"></div>
            <div className="intro-skl-lines"></div>
            <div className="intro-skl-lines"></div>
            <div className="intro-skl-lines"></div>
            <div className="intro-skl-lines"></div>
            {/* <div className="intro-skl-lines short"></div> */}
          </div>
        )}
      </p>
      {data.category === "tools" &&
        ((data?.price && data.price.trim() !== "") ||
          (Array.isArray(data?.platform) && data.platform.length > 0)) && (
          <div className="card-tags-box">
            {data?.price && data.price.trim() !== "" && (
              <SvgIcn name={data?.price.toLowerCase()} />
            )}
            {Array.isArray(data?.platform) &&
              data.platform.length > 0 &&
              data.platform.map((tag, index) => (
                <SvgIcn key={index} name={tag.toLowerCase()} />
              ))}
          </div>
        )}
      {isAppsCard && (appStoreUrl || playStoreUrl) && (
        <div className="card-btn-box apps-store-buttons">
          {appStoreUrl && (
            <button
              type="button"
              className="platform-link-btn"
              onClick={(e) => handleStoreClick(e, "apple", appStoreUrl)}
              aria-label="Open in App Store"
            >
              <SvgIcn name="apple" />
            </button>
          )}
          {playStoreUrl && (
            <button
              type="button"
              className="platform-link-btn"
              onClick={(e) => handleStoreClick(e, "android", playStoreUrl)}
              aria-label="Open in Google Play"
            >
              <SvgIcn name="android" />
            </button>
          )}
        </div>
      )}
      {Array.isArray(data?.tags) && data?.tags.length > 0 && (
        <div className="card-tags-box" ref={tagsRowRef}>
          {(hideLastTag ? data.tags.slice(0, -1) : data.tags).map((tag, index) => (
            <TagZ key={index} label={tag} />
          ))}
        </div>
      )}

      {/* Conditionally render icons based on visibleInputs */}
    </div>
  );
};

export default ItemCard;
